import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "onClick", "children"];
import React, { useContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
import { SelectableContext } from './DropdownMenuContext';
import Tag from '../Tag';
import { isFunction } from '../helper';
const SelectedStyle = css(["outline:none;background-color:transparent;color:var(--primary-text);svg{fill:var(--primary-icon);}"]);
const DisabledStyle = css(["opacity:0.6;cursor:not-allowed;color:var(--neutral-text-disabled);&:hover,&:active,&:focus{background-color:transparent;color:var(--neutral-text-disabled);svg{fill:var(--neutral-icon-disabled);}}"]);
const Root = styled.a.withConfig({
  displayName: "DropdownMenuItem__Root",
  componentId: "sc-1un89ul-0"
})(["", " display:flex;align-items:flex-end;padding:var(--size-spacing-03) var(--size-spacing-04);color:var(--neutral-text-high-emphasis);font:var(--text-md-bold);flex:1;cursor:pointer;white-space:nowrap;text-decoration:none;svg{fill:var(--neutral-icon-low-emphasis);margin:0;}.text{margin:0;}.text + svg,svg + .text{margin-left:var(--size-spacing-02);}.text + .tag,.tag + .text{margin-left:var(--size-spacing-04);}&:hover,&:active,&:focus{outline:none;background-color:var(--neutral-surface-hover);color:var(--neutral-text-high-emphasis);svg{fill:var(--neutral-icon-low-emphasis);}}", " ", ""], ScopedCssBaseline, _ref => {
  let {
    selected
  } = _ref;
  return selected && css(["", " &:hover,&:active,&:focus{", "}"], SelectedStyle, SelectedStyle);
}, _ref2 => {
  let {
    disabled
  } = _ref2;
  return disabled && css(["", ""], DisabledStyle);
});

function resolveChildren(children) {
  if (!children || !React.Children.count(children)) return;
  return React.Children.map(children, child => {
    if (typeof child === 'string') {
      return /*#__PURE__*/React.createElement("span", {
        className: "text"
      }, child);
    }

    if (child.type === Tag) {
      return /*#__PURE__*/React.cloneElement(child, {
        className: 'tag'
      });
    }

    return child;
  });
}

const DropdownMenuItem = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
    disabled,
    onClick,
    children
  } = _ref3,
      props = _objectWithoutPropertiesLoose(_ref3, _excluded);

  const select = useContext(SelectableContext);

  const handleClick = event => {
    if (disabled) return;
    isFunction(select) && select(event);
    isFunction(onClick) && onClick(event);
  };

  const handleKeyDown = event => {
    if (event.key === ' ' || event.key === 'Enter') handleClick(event);
  };

  return /*#__PURE__*/React.createElement(Root, _extends({
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    role: "menuitem",
    tabIndex: "-1",
    "aria-disabled": disabled,
    disabled: disabled,
    ref: ref
  }, props), resolveChildren(children));
});
DropdownMenuItem.Style = Root;
DropdownMenuItem.defaultProps = {
  as: 'a',
  selected: false,
  disabled: false
};
process.env.NODE_ENV !== "production" ? DropdownMenuItem.propTypes = {
  /**
   * Set element type to be rendered for this component. By default it will be render a `<a>` element.
   */
  as: PropTypes.elementType,

  /**
   * Set boolean value to define item selected
   */
  selected: PropTypes.bool,

  /**
   * Disable the item, making it unselectable
   */
  disabled: PropTypes.bool,

  /**
   * Sets the children element
   */
  children: PropTypes.node,

  /**
   * Callback fired when the item is clicked.
   */
  onClick: PropTypes.func
} : void 0;
DropdownMenuItem.displayName = 'DropdownMenu.Item';
export default DropdownMenuItem;